import React from "react";
import { Button, Popover } from "@arco-design/web-react";
import { IconCheck } from "@arco-design/web-react/icon";

// FlowGraph draws the flow's steps as a tree laid out left to right: each
// step, then the step after it; where there's a choice, the ways on stacked,
// each going on as far as it goes. Each step shows how it stands: the current
// one, done, or not yet — and whether
// clicking it goes back there or does it next. Hovering a step explains the
// settings behind it, with a way to change them.
//
// mode "build" (the campaign page, while editing): the flow as it's set up,
// every step plain, and clicking one opens the settings behind it. mode
// "test" (Test drive): how far the test got, and clicking plays it.
export default function FlowGraph({ graph, status = {}, current = null, busy = "", onNode, onFix, mode = "test" }) {
  const build = mode === "build";
  const kids = (key) => graph.filter((n) => n.parent === key);
  const root = graph.find((n) => !n.parent);
  const node = (n) => (
    <GraphNode
      key={n.key}
      node={n}
      state={build ? "" : status[n.key]}
      build={build}
      // the next step from the current one: clicking does it
      next={!build && !status[n.key] && n.parent === current}
      busy={busy === n.key}
      onClick={() => onNode(n.key)}
      onFix={onFix}
    />
  );
  const chain = (n) => {
    const next = kids(n.key);
    return (
      <div key={n.key} style={{ display: "flex", alignItems: "center" }}>
        {node(n)}
        {next.length === 1 && <><Line />{chain(next[0])}</>}
        {next.length > 1 && (
          <>
            <Line />
            <div style={{ display: "flex", flexDirection: "column", gap: 6, borderLeft: "2px solid var(--color-border-3)", padding: "2px 0" }}>
              {next.map((c) => (
                <div key={c.key} style={{ display: "flex", alignItems: "center" }}>
                  <Line short />
                  {chain(c)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };
  return (
    <div style={{ border: "1px solid var(--color-border-2)", borderRadius: 8, padding: "12px 12px 8px", background: "var(--color-bg-2)", overflowX: "auto" }}>
      <div style={{ minWidth: "min-content" }}>{root && chain(root)}</div>
      {build ? (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--color-text-3)" }}>
          Every way through your signup flow, as it's set up below (saved or not). Hover a step to see why it's there; click it to go to its settings.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 11, color: "var(--color-text-3)" }}>
          <Legend color="rgb(var(--arcoblue-6))" filled>Now</Legend>
          <Legend color="rgb(var(--green-6))" filled>Done</Legend>
          <Legend color="rgb(var(--arcoblue-4))" dashed>Next: click to do it</Legend>
          <span>Click a step you've done to go back to it, as it was then (what came after is cleared).</span>
        </div>
      )}
    </div>
  );
}

function Line({ short }) {
  return <div style={{ width: short ? 10 : 16, height: 2, background: "var(--color-border-3)", flex: "none" }} />;
}

function GraphNode({ node, state, build, next, busy, onClick, onFix }) {
  const look = node.blocked ? { border: "2px solid rgb(var(--red-6))", background: "rgb(var(--red-1))", color: "rgb(var(--red-7))" }
    : node.unreachable ? { border: "2px dotted var(--color-border-2)", background: "var(--color-fill-1)", color: "var(--color-text-4)", opacity: 0.7 }
    : node.muted ? { border: "2px dotted var(--color-border-3)", background: "var(--color-fill-1)", color: "var(--color-text-3)" }
    : node.caution && (build || !state) ? { border: "2px solid rgb(var(--orange-5))", background: "rgb(var(--orange-1))", color: "var(--color-text-1)" }
    : build ? { border: "2px solid var(--color-border-3)", background: "var(--color-bg-2)", color: "var(--color-text-1)" } : {
    current: { border: "2px solid rgb(var(--arcoblue-6))", background: "rgb(var(--arcoblue-6))", color: "#fff" },
    done: { border: "2px solid rgb(var(--green-6))", background: "rgb(var(--green-1))", color: "var(--color-text-1)" },
    elsewhere: { border: "2px solid rgb(var(--green-4))", background: "var(--color-bg-2)", color: "var(--color-text-2)" },
    "": { border: `2px dashed ${next ? "rgb(var(--arcoblue-4))" : "var(--color-border-3)"}`, background: "var(--color-bg-2)", color: next ? "var(--color-text-1)" : "var(--color-text-3)" },
  }[state || ""];
  const hint = node.blocked ? "Nothing after this can happen until it's fixed."
    : node.unreachable ? "Out of reach: the signup email can't be sent."
    : build ? (node.fix ? "Click to go to its settings." : "")
    : node.muted ? "" : state ? "Click to go back to this step." : next ? "Click to do this next." : "Click to go to the step before it, then do this.";
  const reachable = !node.blocked && !node.unreachable;
  const why = (
    <div style={{ maxWidth: 300, fontSize: 12, lineHeight: 1.5 }}>
      <div style={node.blocked ? { color: "rgb(var(--red-6))" } : undefined}>{node.why}</div>
      {node.caution && <div style={{ marginTop: 6, color: "rgb(var(--orange-6))" }}>{node.caution}</div>}
      {hint && <div style={{ marginTop: 6, color: "var(--color-text-3)" }}>{hint}</div>}
      {node.fix && onFix && (
        <Button size="mini" type="text" style={{ padding: 0, marginTop: 4 }} onClick={() => onFix(node.fix)}>Change it →</Button>
      )}
    </div>
  );
  return (
    <Popover trigger="hover" position="bottom" title={node.label} content={why}>
    <button
      type="button"
      onClick={onClick}
      aria-label={node.label}
      data-state={node.blocked ? "blocked" : node.unreachable ? "unreachable" : state || (next ? "next" : "")}
      disabled={busy}
      style={{
        ...look, borderRadius: 8, padding: "6px 10px", minWidth: 118, maxWidth: 170, textAlign: "left", cursor: busy ? "wait" : "pointer",
        display: "flex", flexDirection: "column", gap: 1, lineHeight: 1.25,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600 }}>
        {(state === "done" || state === "elsewhere") && <IconCheck style={{ flex: "none" }} />}
        {node.blocked && <span aria-hidden>⛔</span>}
        {node.caution && !node.blocked && <span aria-hidden>⚠</span>}
        {next && !node.muted && reachable && <span aria-hidden>▶</span>}
        {busy ? "…" : node.label}
      </span>
      {node.desc && <span style={{ fontSize: 11, opacity: 0.8 }}>{node.desc}</span>}
    </button>
    </Popover>
  );
}

function Legend({ color, filled, dashed, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: filled ? color : "transparent", border: `2px ${dashed ? "dashed" : "solid"} ${color}` }} />
      {children}
    </span>
  );
}
